import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import dayjs from "dayjs";

const useStyles = makeStyles({
  card: {
    marginBottom: 12,
    border: "1px solid #e0e0e0",
    boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.14)",
  },
  cardContent: {
    paddingBottom: 8,
  },
  header: {
    marginBottom: 12,
  },
  memberName: {
    fontWeight: 600,
    fontSize: 16,
    color: "#006EB3",
  },
  infoGrid: {
    marginTop: 8,
  },
  label: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  value: {
    fontWeight: 500,
    fontSize: 14,
  },
  cardActions: {
    padding: "8px 16px",
    paddingTop: 0,
  },
  lmsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
});

interface GroupMemberCardProps { member: { email?: React.ReactNode; first_name?: string; hasAssessments?: boolean; hasPending?: boolean; last_name?: string; lms_accessed?: boolean; start_date?: string; [key: string]: React.ReactNode | boolean | undefined }; onViewAssignments?: () => void; }

export default function GroupMemberCard({ member, onViewAssignments }: GroupMemberCardProps): React.ReactElement {
  const classes = useStyles();

  const fullName = member.first_name + " " + member.last_name;
  const createdDate = dayjs(member.start_date).format("DD/MM/YYYY");

  const getStatusChip = () => {
    if (!member.hasAssessments) {
      return (
        <Chip
          label="None Assigned"
          size="small"
          style={{
            background: "#fff",
            color: "#b94946",
            border: "1px solid #b94946",
            fontWeight: "bold",
          }}
        />
      );
    } else if (member.hasPending) {
      return (
        <Chip
          label="Pending"
          size="small"
          style={{
            background: "#fcb316",
            color: "#fff",
          }}
        />
      );
    } else {
      return (
        <Chip
          label="All Completed"
          size="small"
          style={{
            background: "#03aab1",
            color: "#fff",
          }}
        />
      );
    }
  };

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <Typography className={classes.memberName}>{fullName}</Typography>
        </div>

        <Grid container spacing={2} className={classes.infoGrid}>
          <Grid item xs={12}>
            <Typography className={classes.label}>Email</Typography>
            <Typography className={classes.value}>{member.email}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>Created</Typography>
            <Typography className={classes.value}>{createdDate}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>LMS Accessed</Typography>
            <div className={classes.lmsContainer}>
              {member.lms_accessed ? (
                <>
                  <CheckCircleIcon style={{ color: "#49a87b", fontSize: 20 }} />
                  <Typography className={classes.value}>Yes</Typography>
                </>
              ) : (
                <>
                  <CancelIcon style={{ color: "#c73c3c", fontSize: 20 }} />
                  <Typography className={classes.value}>No</Typography>
                </>
              )}
            </div>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.label}>Assignment Status</Typography>
            {getStatusChip()}
          </Grid>
        </Grid>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          disabled={!member.hasAssessments}
          onClick={onViewAssignments}
        >
          View Assignments
        </Button>
      </CardActions>
    </Card>
  );
}
